import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  inject,
} from '@angular/core';

@Directive({
  selector: '[fittext]',
  standalone: true,
})
export class SiaFittextDirective
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  @Input() compression = 1;
  @Input() activateOnResize? = true;
  @Input() minFontSize: number | 'inherit' = 0;
  @Input() maxFontSize: number | 'inherit' = Number.POSITIVE_INFINITY;
  @Input() delay? = 100;
  @Input() innerHTML: any;
  @Input() fontUnit?: 'px' | 'em' | string = 'px';
  @Input() set resize(dimensions: { width: number; height: number }) {
    // console.log(dimensions);
    if (
      dimensions.width != this._previousWidth ||
      dimensions.height != this._previousHeight
    ) {
      this.setFontSize();
    }
  }

  private _fittextParent: HTMLElement | null | undefined;
  private _fittextElement: HTMLElement | null | undefined;
  private _fittextMinFontSize: number = 0;
  private _fittextMaxFontSize: number = Number.POSITIVE_INFINITY;
  private _computed: CSSStyleDeclaration | undefined;
  private _newlines: number = 0;
  private _lineHeight: string = '';
  private _display: string = '';
  private _calcSize = 10;
  private _previousWidth: number = 0;
  private _previousHeight: number = 0;

  @HostListener('window:unload')
  ngOnDestroy() {
    this.el.nativeElement.remove();
    this._fittextParent?.remove();
    this.compression = 0;
    this.minFontSize = 0;
    this.maxFontSize = 0;
    this.delay = undefined;
    this.innerHTML = null;
    this.fontUnit = undefined;
    this._fittextParent = null;
    this._fittextElement = null;
    this._fittextMinFontSize = 0;
    this._fittextMaxFontSize = 0;
    this._computed = undefined;
    this._newlines = 0;
    this._lineHeight = 'null';
    this._display = 'null';
    this._calcSize = 0;
    this._previousWidth = 0;
    this._previousHeight = 0;
    this._timeouts.forEach((t) => {
      clearTimeout(t);
    });
    this._timeouts = [];
  }

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    const el = this.el;

    this._fittextElement = el.nativeElement;
    if (!this._fittextElement) return;
    this._computed = window.getComputedStyle(this._fittextElement);
  }
  private _timeouts: any[] = [];

  @HostListener('window:resize')
  public onWindowResize = (): void => {
    if (this.activateOnResize) {
      this.setFontSize();
    }
  };

  public ngOnInit() {
    if (this._computed) {
      this._fittextMinFontSize =
        this.minFontSize === 'inherit'
          ? parseInt(this._computed.fontSize.toString())
          : this.minFontSize;
      this._fittextMaxFontSize =
        this.maxFontSize === 'inherit'
          ? parseInt(this._computed.fontSize.toString())
          : this.maxFontSize;
    }
  }

  public ngAfterViewInit() {
    this.setFontSize(0);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['compression'] && !changes['compression'].firstChange) {
      this.setFontSize(0);
    }
    if (changes['innerHTML']) {
      if (this._fittextElement) {
        this._fittextElement.innerHTML = this.innerHTML;
        if (!changes['innerHTML'].firstChange) {
          this.setFontSize(0);
        }
      }
    }
  }

  private setFontSize = (delay: number = this.delay ?? 0): void => {
    let t = setTimeout(
      (() => {
        if (
          this._fittextElement &&
          this._fittextElement.offsetHeight *
            this._fittextElement.offsetWidth !==
            0
        ) {
          this.calculateNewFontSize();
        }
      }).bind(this),
      delay,
    );
    this._timeouts.push(t);
  };

  private calculateNewFontSize = () => {
    if (!this._fittextElement || !this._fittextElement.parentElement) return;
    const parent = this._fittextElement.parentElement;
    const text = this._fittextElement;

    // Get computed styles for padding
    const parentStyles = window.getComputedStyle(parent);
    const parentPaddingTop = parseFloat(parentStyles.paddingTop);
    const parentPaddingBottom = parseFloat(parentStyles.paddingBottom);

    // Calculate available height inside the parent
    const availableHeight =
      parent.clientHeight - parentPaddingTop - parentPaddingBottom;

    let fontSize = 10; // Start with a small font size
    text.style.fontSize = `${fontSize}px`;

    // Increase font size until it overflows the available height
    while (text.scrollHeight <= availableHeight && fontSize < 500) {
      fontSize++;
      text.style.fontSize = `${fontSize}px`;
    }

    // Revert to last fitting size
    text.style.fontSize = `${fontSize - 1}px`;
  };
}
